import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import * as Sentry from '@sentry/react';
import {
  resolveFeatureMetadata,
  type FeatureSpanMetadata,
} from './sentry.router';
import { recordHttpClientMetric } from './sentry.metrics';
import { shouldCreateHttpClientSpan } from './sentry.sampling';
import { syncSentryUserContext } from './sentry.tags';

type TrackedAxiosConfig = InternalAxiosRequestConfig & {
  sentryStartedAt?: number;
  sentrySpan?: {
    end: () => void;
    setAttribute?: (key: string, value: string | number) => void;
  };
  sentryFeature?: FeatureSpanMetadata;
};

function redactHeaders(
  headers: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!headers) {
    return undefined;
  }

  return Object.entries(headers).reduce<Record<string, unknown>>(
    (accumulator, [key, value]) => {
      const normalizedKey = key.toLowerCase();
      accumulator[key] =
        normalizedKey.includes('authorization') ||
        normalizedKey.includes('cookie')
          ? '[REDACTED]'
          : value;
      return accumulator;
    },
    {},
  );
}

function shouldCaptureAxiosError(error: AxiosError): boolean {
  if (!error.response) {
    return true;
  }

  return error.response.status >= 500;
}

function resolveFeatureFromRequest(
  url: string | undefined,
): FeatureSpanMetadata | null {
  if (!url) {
    return null;
  }

  if (url.includes('upload')) {
    return {
      route: url,
      module: 'documents',
      feature: 'upload',
      op: 'http.client.upload',
    };
  }

  if (url.includes('/tech-systems')) {
    return {
      route: url,
      module: 'tech-systems',
      feature: 'data-load',
      op: 'http.client',
    };
  }

  if (url.includes('/documents')) {
    return {
      route: url,
      module: 'documents',
      feature: 'data-load',
      op: 'http.client',
    };
  }

  if (url.includes('/dashboard')) {
    return {
      route: url,
      module: 'dashboard',
      feature: 'data-load',
      op: 'http.client',
    };
  }

  if (url.includes('/tasks') || url.includes('/notion')) {
    return {
      route: url,
      module: 'task-management',
      feature: 'data-load',
      op: 'http.client',
    };
  }

  return resolveFeatureMetadata(url);
}

function finishSpan(config: TrackedAxiosConfig | undefined, status?: number): void {
  if (!config?.sentrySpan) {
    return;
  }

  if (typeof status === 'number') {
    config.sentrySpan.setAttribute?.('http.status_code', status);
  }

  config.sentrySpan.end();
  delete config.sentrySpan;
}

function resolveRequestDuration(config: TrackedAxiosConfig | undefined): number {
  if (typeof config?.sentryStartedAt !== 'number') {
    return 0;
  }

  return Math.max(Date.now() - config.sentryStartedAt, 0);
}

function recordAxiosMetric(
  config: TrackedAxiosConfig | undefined,
  input: {
    statusCode?: number;
    outcome: 'success' | 'error';
  },
): void {
  recordHttpClientMetric({
    method: config?.method?.toUpperCase() ?? 'GET',
    statusCode: input.statusCode,
    outcome: input.outcome,
    durationMs: resolveRequestDuration(config),
    module: config?.sentryFeature?.module,
    feature: config?.sentryFeature?.feature,
  });
}

function attachFeatureSpan(config: TrackedAxiosConfig): TrackedAxiosConfig {
  config.sentryStartedAt = Date.now();
  if (!shouldCreateHttpClientSpan(config.url)) {
    return config;
  }

  const feature = resolveFeatureFromRequest(config.url);
  config.sentryFeature = feature ?? undefined;

  if (!feature) {
    return config;
  }

  config.sentrySpan = Sentry.startInactiveSpan({
    op: feature.op ?? 'http.client',
    name: `${feature.module}.${feature.feature}`,
    attributes: {
      route: feature.route,
      module: feature.module,
      feature: feature.feature,
      method: config.method?.toUpperCase() ?? 'GET',
    },
  });

  return config;
}

export function syncSentryAuthContext(): void {
  syncSentryUserContext();
}

export function attachAxiosSentryHandlers(api: AxiosInstance): void {
  api.interceptors.request.use((requestConfig) => {
    syncSentryAuthContext();
    return attachFeatureSpan(requestConfig as TrackedAxiosConfig);
  });

  api.interceptors.response.use(
    (response) => {
      finishSpan(response.config as TrackedAxiosConfig, response.status);
      recordAxiosMetric(response.config as TrackedAxiosConfig, {
        statusCode: response.status,
        outcome: 'success',
      });
      return response;
    },
    (error: AxiosError) => {
      const requestConfig = error.config as TrackedAxiosConfig | undefined;
      const feature = requestConfig?.sentryFeature;
      finishSpan(requestConfig, error.response?.status);
      recordAxiosMetric(requestConfig, {
        statusCode: error.response?.status,
        outcome: 'error',
      });

      if (shouldCaptureAxiosError(error)) {
        Sentry.withScope((scope) => {
          if (feature) {
            scope.setTag('module', feature.module);
            scope.setTag('feature', feature.feature);
          }

          scope.setLevel('error');
          scope.setContext('http', {
            method: requestConfig?.method?.toUpperCase() ?? 'GET',
            url: requestConfig?.url ?? '',
            status: error.response?.status ?? null,
            headers: redactHeaders(
              requestConfig?.headers as Record<string, unknown> | undefined,
            ),
            response: error.response?.data ?? null,
          });
          Sentry.captureException(error);
        });
      }

      return Promise.reject(error);
    },
  );
}
